import classNames from 'classnames';

import lang from '../../../lib/language';
import { NullableString } from '../../../lib/util/types';

import shareLinkIcon from '../../../assets/img/icon-share-link.svg';
import { GlobalClassesEnum } from '../../../assets/scss/globalClasses/globalClasses.constants';
import { buildReferralShareUrl, buildSocialShareUrl } from '../../../containers/Referrals/helpers';
import { SHARE_POPUP_FEATURES } from '../../../containers/Referrals/Referrals.constants';
import { SocialNetworkEnum } from '../../../containers/Referrals/Referrals.types';
import { useIsMobile } from '../../../hooks';
import BaseModal from '../../../shared_components/BaseModal/BaseModal';
import Button from '../../../shared_components/Button/Button';
import MobileDrawer from '../../../shared_components/MobileDrawer/MobileDrawer';

import {
  QR_CODE_DATA_URI_PREFIX,
  QR_CODE_SIZE,
  SHARE_MODAL_WIDTH,
  SOCIAL_NETWORKS,
} from './ReferralShareModal.constants';

import styles from './ReferralShareModal.module.scss';

type Props = {
  isOpen: boolean;
  link: string;
  qrImageBase64: NullableString;
  onClose: () => void;
};

const secondaryClasses = classNames(
  GlobalClassesEnum.CommonBody2Text,
  GlobalClassesEnum.CommonTextSecondary,
);

const ReferralShareModal = ({ isOpen, link, qrImageBase64, onClose }: Props) => {
  const { isMobile } = useIsMobile();
  const shareUrl = buildReferralShareUrl(link);

  const handleSocialShare = (network: SocialNetworkEnum) => {
    window.open(buildSocialShareUrl(network, link), '_blank', SHARE_POPUP_FEATURES);
  };

  const handleNativeShare = async () => {
    if (!navigator.share) {
      window.open(shareUrl, '_blank', SHARE_POPUP_FEATURES);

      return;
    }

    try {
      await navigator.share({ title: lang.referralsShareModalTitle(), url: shareUrl });
    } catch (error) {
      // The share sheet was dismissed by the user - nothing to recover from.
    }
  };

  // `own/qr` 404s when the QR is disabled for the referrer, so there may be nothing to show.
  const qrCode = qrImageBase64 ? (
    <div className={styles.qrContainer}>
      <img
        src={`${QR_CODE_DATA_URI_PREFIX}${qrImageBase64}`}
        width={QR_CODE_SIZE}
        height={QR_CODE_SIZE}
        alt="qr-code"
      />
    </div>
  ) : null;

  if (isMobile) {
    return (
      <MobileDrawer isOpen={isOpen} title='' onClose={onClose}>
        <div className={styles.title}>
          {lang.referralsShareModalTitle()}
        </div>
        <div className={styles.sheetBody}>
          <div className={classNames(secondaryClasses, styles.subtitle)}>{lang.referralsShareModalSubtitle()}</div>
          {qrCode}
          <Button onClick={handleNativeShare} biggerFontSize>
            <span className={styles.shareLinkContent}>
              <img src={shareLinkIcon} alt="share" />
              {lang.referralsShareModalShareLink()}
            </span>
          </Button>
        </div>
      </MobileDrawer>
    );
  }

  return (
    <BaseModal isOpen={isOpen} toggle={onClose} width={SHARE_MODAL_WIDTH} contentClassName={styles.modal}>
      <div className={styles.header}>
        <div className={styles.title}>{lang.referralsShareModalTitle()}</div>
        <div className={classNames(secondaryClasses, styles.subtitle)}>{lang.referralsShareModalSubtitle()}</div>
      </div>
      {qrCode}
      <div className={styles.socialSection}>
        <div className={classNames(secondaryClasses, styles.socialLabel)}>{lang.referralsShareModalSocialLabel()}</div>
        <div className={styles.socialActions}>
          {SOCIAL_NETWORKS.map(({ network, icon, ariaLabel }) => (
            <button
              type="button"
              key={network}
              className={styles.socialButton}
              aria-label={ariaLabel}
              onClick={() => handleSocialShare(network)}
            >
              <img src={icon} alt="" />
            </button>
          ))}
        </div>
      </div>
    </BaseModal>
  );
};

export default ReferralShareModal;
